import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import ProductList from "./components/ProductList";
import ProductDetail from "./components/ProductDetail";
import Dashboard from "./components/Dashboard";
import {
  getProducts,
  getStats,
  enrichProduct,
  enrichAll,
  getDashboard,
} from "./api";

function AppHeader() {
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState({ done: 0, total: 0 });
  const [pendingCount, setPendingCount] = useState(null);

  // Fetch pending count on mount
  useEffect(() => {
    getDashboard()
      .then((r) => {
        const stats = r.data.enrichment_stats;
        if (stats) setPendingCount(stats.pending);
      })
      .catch(() => {});
  }, []);

  const handleEnrichAll = async () => {
    setEnriching(true);
    let totalDone = 0;
    let totalNotFound = 0;
    let totalFailed = 0;
    const batchSize = 50;

    try {
      // Get initial count
      const dashRes = await getDashboard();
      const totalPending = dashRes.data.enrichment_stats?.pending || 0;
      setEnrichProgress({ done: 0, total: totalPending });

      if (totalPending === 0) {
        toast.success("All products already enriched!");
        setEnriching(false);
        return;
      }

      // Process in batches
      while (true) {
        const res = await enrichAll(batchSize);
        const { enriched, not_found, failed, total_eligible } = res.data;
        totalDone += enriched;
        totalNotFound += not_found;
        totalFailed += failed;
        setEnrichProgress({
          done: totalDone + totalNotFound + totalFailed,
          total: totalPending,
        });
        // Stop if nothing was processed or nothing remains
        if (enriched === 0 && not_found === 0 && failed === 0) break;
        if (total_eligible - (enriched + not_found + failed) <= 0) break;
      }

      toast.success(
        `Done! ${totalDone} enriched, ${totalNotFound} not found, ${totalFailed} failed.`,
      );
      setPendingCount(0);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Unknown error";
      toast.error("Enrichment failed: " + message);
    } finally {
      setEnriching(false);
      setEnrichProgress({ done: 0, total: 0 });
    }
  };

  const buttonText = enriching
    ? `${enrichProgress.done}/${enrichProgress.total} enriched...`
    : pendingCount !== null && pendingCount > 0
      ? `💿 Enrich All (${pendingCount})`
      : "💿 Enrich All";

  return (
    <header>
      <h1>
        <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>
          Rough Trade <span>Explorer</span>
        </Link>
      </h1>
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          className="btn btn-discogs"
          onClick={handleEnrichAll}
          disabled={enriching}
        >
          {buttonText}
        </button>
        <Link
          to={isDashboard ? "/" : "/dashboard"}
          className="btn btn-secondary"
        >
          {isDashboard ? "← Catalog" : "📊 Dashboard"}
        </Link>
      </div>
    </header>
  );
}

function AppContent() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState("-created_at_shopify");
  const [detailProduct, setDetailProduct] = useState(null);

  // Filters
  const [filterSigned, setFilterSigned] = useState("");
  const [filterInStock, setFilterInStock] = useState("");
  const [filterExclusive, setFilterExclusive] = useState("");
  const [filterPreOrder, setFilterPreOrder] = useState("");
  const [filterSale, setFilterSale] = useState("");
  const [filterGenre, setFilterGenre] = useState("");

  const observerRef = useRef();
  const loadingRef = useRef(false);

  const fetchProducts = useCallback(
    async (pageNum, append = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const params = { page: pageNum, ordering };
        if (search) params.search = search;
        if (filterSigned) params.signed = filterSigned;
        if (filterInStock) params.inventory_available = filterInStock;
        if (filterExclusive) params.exclusive = filterExclusive;
        if (filterPreOrder) params.is_pre_order = filterPreOrder;
        if (filterSale) params.is_on_sale = filterSale;
        if (filterGenre) params.genre__icontains = filterGenre;

        const res = await getProducts(params);
        const data = res.data;
        const results = data.results || [];

        if (append) {
          setProducts((prev) => [...prev, ...results]);
        } else {
          setProducts(results);
        }
        setHasMore(!!data.next);
        setPage(pageNum);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [
      search,
      ordering,
      filterSigned,
      filterInStock,
      filterExclusive,
      filterPreOrder,
      filterSale,
      filterGenre,
    ],
  );

  // Initial load + filter/search changes
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    fetchProducts(1, false);
  }, [fetchProducts]);

  // Fetch stats
  useEffect(() => {
    getStats()
      .then((r) => setStats(r.data))
      .catch(console.error);
  }, []);

  // Infinite scroll
  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
            fetchProducts(page + 1, true);
          }
        },
        { rootMargin: "200px" },
      );
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, page, fetchProducts],
  );

  const handleSort = (field) => {
    // Fields where descending is the natural first-click order
    const descFirst = [
      "inventory_quantity",
      "price_eur",
      "created_at_shopify",
      "release_date",
    ];
    setOrdering((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return field;
      return descFirst.includes(field) ? `-${field}` : field;
    });
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            {/* Stats bar */}
            {stats && (
              <div className="stats-bar">
                <div className="stat-item">
                  <span className="stat-value">{stats.total}</span>
                  <span className="stat-label">Products</span>
                </div>
                <div className="stat-item signed-stat">
                  <span className="stat-value">{stats.signed}</span>
                  <span className="stat-label">✍️ Signed</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.exclusive}</span>
                  <span className="stat-label">Exclusive</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.in_stock}</span>
                  <span className="stat-label">In Stock</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.pre_order}</span>
                  <span className="stat-label">Pre-Order</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    €{stats.avg_price_eur?.toFixed(2) || "0"}
                  </span>
                  <span className="stat-label">Avg Price</span>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="filters-bar">
              <input
                type="text"
                placeholder="Search title, artist, label, barcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
              <button
                className={`filter-btn ${filterSigned === "true" ? "active signed-active" : ""}`}
                onClick={() =>
                  setFilterSigned((p) => (p === "true" ? "" : "true"))
                }
              >
                ✍️ Signed Only
              </button>
              <button
                className={`filter-btn ${filterInStock === "true" ? "active" : ""}`}
                onClick={() =>
                  setFilterInStock((p) => (p === "true" ? "" : "true"))
                }
              >
                In Stock
              </button>
              <button
                className={`filter-btn ${filterExclusive === "true" ? "active" : ""}`}
                onClick={() =>
                  setFilterExclusive((p) => (p === "true" ? "" : "true"))
                }
              >
                Exclusive
              </button>
              <button
                className={`filter-btn ${filterPreOrder === "true" ? "active" : ""}`}
                onClick={() =>
                  setFilterPreOrder((p) => (p === "true" ? "" : "true"))
                }
              >
                Pre-Order
              </button>
              <button
                className={`filter-btn ${filterSale === "true" ? "active" : ""}`}
                onClick={() =>
                  setFilterSale((p) => (p === "true" ? "" : "true"))
                }
              >
                On Sale
              </button>
              <input
                type="text"
                placeholder="Genre..."
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                className="genre-input"
              />
            </div>

            <ProductList
              products={products}
              loading={loading}
              lastProductRef={lastProductRef}
              onSort={handleSort}
              ordering={ordering}
              onRowClick={setDetailProduct}
            />

            {detailProduct && (
              <ProductDetail
                product={detailProduct}
                onClose={() => setDetailProduct(null)}
                onEnrich={async (id) => {
                  const res = await enrichProduct(id);
                  toast.success(
                    `Enriched! Hotness: ${res.data.hotness_score?.toFixed(1) || "N/A"}`,
                  );
                  // Refresh the product in the list
                  const updated = await getProducts({
                    search: detailProduct.barcode,
                  });
                  const found = updated.data.results?.find((p) => p.id === id);
                  if (found) {
                    setDetailProduct(found);
                    setProducts((prev) =>
                      prev.map((p) => (p.id === id ? found : p)),
                    );
                  }
                }}
              />
            )}
          </>
        }
      />
      <Route path="/dashboard" element={<Dashboard />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <Toaster position="top-right" />
      <div className="app-container">
        <AppHeader />
        <AppContent />
      </div>
    </Router>
  );
}
